import _ from 'lodash';
import { CognitoWrapperService } from 'src/app/core/services/congito.wrapper.service';
import { environment } from '../../../../environments/environment';
import { ReportListResponse } from './types';

export class ResolveApiClient {
  private _clinicToken: string;
  private _authService: CognitoWrapperService;
  private _onUnauthorized: () => void;

  constructor(props: {
    clinicToken: string;
    authService: CognitoWrapperService;
    onUnauthorized: () => void;
  }) {
    this._clinicToken = props.clinicToken;
    this._authService = props.authService;
    this._onUnauthorized = props.onUnauthorized;
  }

  private static toCamelCase(obj: any) {
    let newObj: any;

    if (obj instanceof Array) {
      return obj.map((value) => {
        if (typeof value === 'object') {
          value = ResolveApiClient.toCamelCase(value);
        }
        return value;
      });
    } else {
      newObj = {};
      for (const origKey in obj) {
        if (obj.hasOwnProperty(origKey)) {
          const newKey = (
            origKey.charAt(0).toLowerCase() + origKey.slice(1) || origKey
          ).toString();
          let value = obj[origKey];

          if (
            value instanceof Array ||
            (value !== null && value.constructor === Object)
          ) {
            value = ResolveApiClient.toCamelCase(value);
          }
          newObj[newKey] = value;
        }
      }
    }

    return newObj;
  }

  public async getReports({
    page = 1,
    query = '',
    filters = {}
  }): Promise<ReportListResponse> {
    const payload = JSON.stringify({
      Action: 'reports',
      Paging: {
        Page: page,
        PageSize: 20
      }
    });
    return this.fetch({
      body: payload,
      headers: { 'content-type': 'application/json' },
      method: 'POST'
    });
  }

  /**
   * Essentially a wrapper around `fetch` that sets some mandatory headers
   * and behavior.
   */
  private async getDefaultFetchHeaders(
    options?: RequestInit & { headers?: Record<string, string> }
  ) {
    const authUser = await this._authService.getAuthSession();

    // Merge headers for the individual request with mandatory ones
    const headers: Record<string, string> = {
      ...(options != null && options.headers != null ? options.headers : {}),
      accept: 'application/json',
      authorization: authUser.getIdToken().getJwtToken(),
      'x-salve-clinic-token': this._clinicToken
    };

    return headers;
  }

  private async handleFetchResponse(response: Response) {
    if (response.status === 401) {
      this._onUnauthorized();
      throw new Error('Unauthorized');
    }

    if (response.ok) {
      try {
        const content = await response.text();
        const result = content.length > 0 ? JSON.parse(content) : undefined;

        // This is a workaround for the public resolve api returning PascalCase
        return ResolveApiClient.toCamelCase(result);
      } catch (err) {
        throw new Error(`Error parsing body as JSON: ${err.message}`);
      }
    }

    throw new Error('Network response was not ok');
  }

  private async fetch(
    /** Force `headers` to be a simple object for ease of use */
    options?: RequestInit & { headers?: Record<string, string> }
  ) {
    // Merge headers for the individual request with mandatory ones
    const headers: Record<string, string> = await this.getDefaultFetchHeaders(
      options
    );
    const response = await fetch(`${environment.api.resolve.endpoint}`, {
      ...options,
      headers
    });

    return this.handleFetchResponse(response);
  }
}
